import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { LANDING_TYCOON_SYMBOL_SRC } from "@/components/landing/landingHeroConstants";
import { scrollToTarget } from "@/lib/smoothScroll";
import { Skeleton } from "@/components/ui/Skeleton";
import { CollectionPage } from "@/pages/CollectionPage";
import { ComingSoonPage } from "@/pages/ComingSoonPage";
import { HomePage } from "@/pages/HomePage";
import { ProfilePage } from "@/pages/ProfilePage";

/** 배포로 청크 해시가 바뀐 구 세션의 dynamic import 404 — 1회 강제 새로고침으로 복구 */
const retryImport = <T,>(loader: () => Promise<T>): Promise<T> =>
  loader().catch((error: unknown) => {
    const key = "chunk-reload";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      window.location.reload();
      return new Promise<never>(() => {}); // 리로드 중 렌더 억제
    }
    throw error;
  });

// 랜딩(`/`)은 마케팅 전용 스탠드얼론 페이지 — framer-motion/gsap 연출과 폰트가 무거워
// React.lazy로 코드 스플릿해 허브(/home) 번들을 가볍게 유지한다.
const LandingPage = lazy(() =>
  retryImport(() => import("@/pages/LandingPage")).then((m) => ({ default: m.LandingPage })),
);

// 세컨더리 라우트도 코드 스플릿 — 허브 핫패스(Home/Draw/Collection/Profile)는
// eager로 남기고, admin(is_admin 전용)·marketplace·agents(~64 kB min 합산)는
// 첫 방문 시에만 내려받는다. 첫 진입은 RouteLoadingFallback 스켈레톤 한 프레임.
const AdminPage = lazy(() => retryImport(() => import("@/pages/AdminPage")).then((m) => ({ default: m.AdminPage })));
const AgentsPage = lazy(() => retryImport(() => import("@/pages/AgentsPage")).then((m) => ({ default: m.AgentsPage })));
const MarketplacePage = lazy(() =>
  retryImport(() => import("@/pages/MarketplacePage")).then((m) => ({ default: m.MarketplacePage })),
);
const MarketCardPage = lazy(() =>
  retryImport(() => import("@/pages/MarketCardPage")).then((m) => ({ default: m.MarketCardPage })),
);

// ── uiux-renewal 라우트 (.hypercore/prd/uiux-renewal) — 전부 코드 스플릿 ──
const lazyRenewal = <K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => retryImport(loader).then((m) => ({ default: m[name] })));
const OnboardingPage = lazyRenewal(() => import("@/pages/renewal/OnboardingPage"), "OnboardingPage");
const ShopPage = lazyRenewal(() => import("@/pages/renewal/ShopPage"), "ShopPage");
const PackProductPage = lazyRenewal(() => import("@/pages/renewal/PackProductPage"), "PackProductPage");
const BoxProductPage = lazyRenewal(() => import("@/pages/renewal/BoxProductPage"), "BoxProductPage");
const UltraChasePage = lazyRenewal(() => import("@/pages/renewal/UltraChasePage"), "UltraChasePage");
const PackInventoryPage = lazyRenewal(() => import("@/pages/renewal/PackInventoryPage"), "PackInventoryPage");
const MissionsPage = lazyRenewal(() => import("@/pages/renewal/MissionsPage"), "MissionsPage");
const AccountLinksPage = lazyRenewal(() => import("@/pages/renewal/AccountLinksPage"), "AccountLinksPage");
const SeasonPage = lazyRenewal(() => import("@/pages/renewal/SeasonPage"), "SeasonPage");
const SeasonGuidePage = lazyRenewal(() => import("@/pages/renewal/SeasonGuidePage"), "SeasonGuidePage");
const WalletPage = lazyRenewal(() => import("@/pages/renewal/WalletPage"), "WalletPage");
const TopupPage = lazyRenewal(() => import("@/pages/renewal/TopupPage"), "TopupPage");
const CardDetailPage = lazyRenewal(() => import("@/pages/renewal/CardDetailPage"), "CardDetailPage");
const BurnExchangePage = lazyRenewal(() => import("@/pages/renewal/BurnExchangePage"), "BurnExchangePage");
const KycPage = lazyRenewal(() => import("@/pages/renewal/KycPage"), "KycPage");
const RedeemPage = lazyRenewal(() => import("@/pages/renewal/RedeemPage"), "RedeemPage");
const RedemptionsPage = lazyRenewal(() => import("@/pages/renewal/RedemptionsPage"), "RedemptionsPage");

/** BrowserRouter never resets scroll on navigation — every route change starts
 * at the top (immediate, via Lenis when mounted). Hash anchors delegate to
 * lenis.scrollTo when the instance exists (scroll-asset-prd §5). */
function ClearChunkReloadFlag() {
  useEffect(() => sessionStorage.removeItem("chunk-reload"), []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) scrollToTarget(hash);
    else scrollToTarget(0, true);
  }, [pathname, hash]);
  return null;
}

/** `index.html` only paints `#030308` — every visitor hits this chunk first, so a slow
 * connection needs the mark instead of a blank screen while it downloads. */
function LandingLoadingFallback() {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#030308]"
      role="status"
      aria-label="Loading"
    >
      <img
        src={LANDING_TYCOON_SYMBOL_SRC}
        alt=""
        className="h-11 w-auto max-w-[5.5rem] animate-pulse object-contain"
        width={120}
        height={120}
      />
    </div>
  );
}

/** Shell 내부 lazy 라우트의 첫 로드 폴백 — AppShell 크롬은 그대로 둔 채 본문만 스켈레톤. */
function RouteLoadingFallback() {
  return (
    <div className="flex flex-col gap-5" role="status" aria-label="Loading">
      <Skeleton className="h-7 w-44 rounded" />
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  );
}

/** Wraps a lazy shell route so a slow first chunk shows the skeleton, not a blank body. */
function LazyRoute({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

/** 구 /redeem/:cardId → /cards/:cardId/redeem — 파라미터 보존 레거시 리다이렉트 */
function RedeemLegacyRedirect() {
  const { cardId } = useParams<{ cardId: string }>();
  return (
    <Navigate
      to={cardId ? `/cards/${encodeURIComponent(cardId)}/redeem` : "/profile/collection"}
      replace
    />
  );
}

// 서비스 경계 원칙 (docs/MVP_DATA_MODEL.md §-1): tycoonex.com 정식 서비스 SPA.
// pioneer.tycoonex.com(이벤트, apps/pioneer-event)과 완전히 분리 배포한다.
// `/` = 랜딩(셸 밖, docs/WEB_LAYOUT_GUIDE.md §3) · 나머지는 AppShell 안의 <Outlet/>.
export function App() {
  return (
    <BrowserRouter>
      {/* 모바일: 하단 탭바(52px+safe-area)와 겹치지 않게 토스트를 위로 */}
      <Toaster position="bottom-right" richColors closeButton mobileOffset={{ bottom: 76 }} />
      <ScrollToTop />
          <ClearChunkReloadFlag />
      <Routes>
        <Route
          path="/"
          element={
            <Suspense fallback={<LandingLoadingFallback />}>
              <LandingPage />
            </Suspense>
          }
        />
        <Route element={<AppShell />}>
          <Route path="/home" element={<HomePage />} />
          {/* 신규 유저 온보딩 — 비서 선택 → 첫 무료 팩 → 루프 안내 (R-04) */}
          <Route path="/onboarding" element={<LazyRoute><OnboardingPage /></LazyRoute>} />

          {/* ── 상점 서브트리 (R-05) — 허브 /shop 아래 상품 유형별 계층 ── */}
          <Route path="/shop">
            <Route index element={<LazyRoute><ShopPage /></LazyRoute>} />
            <Route path="packs">
              {/* 레거시 피니시 단위 판매 UI 폐기 — 목록은 /shop이 담당 */}
              <Route index element={<Navigate to="/shop" replace />} />
              {/* 오리파 팩 상세(Blez 7블록) — 미존재/구 슬러그는 페이지 내부에서 /shop 리다이렉트 */}
              <Route path=":packId" element={<LazyRoute><PackProductPage /></LazyRoute>} />
            </Route>
            <Route path="boxes">
              <Route index element={<Navigate to="/shop" replace />} />
              <Route path=":boxId" element={<LazyRoute><BoxProductPage /></LazyRoute>} />
            </Route>
            <Route path="ultra-chase" element={<LazyRoute><UltraChasePage /></LazyRoute>} />
          </Route>

          {/* ── 카드 정본 서브트리 — 상점/컬렉션/피드가 공유하는 카드 리소스 + 카드 액션 ── */}
          <Route path="/cards">
            <Route index element={<Navigate to="/profile/collection" replace />} />
            <Route path=":cardId">
              <Route index element={<LazyRoute><CardDetailPage /></LazyRoute>} />
              {/* 자산 출금 신청 — 카드에 대한 액션이므로 카드 하위 (R-12) */}
              <Route path="redeem" element={<LazyRoute><RedeemPage /></LazyRoute>} />
            </Route>
          </Route>

          {/* ── 시즌 서브트리 (R-15~R-18) ── */}
          <Route path="/season">
            <Route index element={<LazyRoute><SeasonPage /></LazyRoute>} />
            <Route path="guide" element={<LazyRoute><SeasonGuidePage /></LazyRoute>} />
          </Route>

          {/* ── 마이 허브 서브트리 — 내 자산/활동은 전부 /profile 아래 (R-12/13/14/19) ── */}
          <Route path="/profile">
            <Route index element={<ProfilePage />} />
            <Route path="packs" element={<LazyRoute><PackInventoryPage /></LazyRoute>} />
            <Route path="collection">
              <Route index element={<CollectionPage />} />
              {/* 소각 교환 — 내 컬렉션에 대한 액션 (구 /burn) */}
              <Route path="burn" element={<LazyRoute><BurnExchangePage /></LazyRoute>} />
            </Route>
            <Route path="missions">
              <Route index element={<LazyRoute><MissionsPage /></LazyRoute>} />
              <Route path="links" element={<LazyRoute><AccountLinksPage /></LazyRoute>} />
            </Route>
            <Route path="wallet">
              <Route index element={<LazyRoute><WalletPage /></LazyRoute>} />
              <Route path="topup" element={<LazyRoute><TopupPage /></LazyRoute>} />
            </Route>
            {/* 출금 내역·계정 인증 — 내 데이터 (구 /redemptions·/kyc) */}
            <Route path="redemptions" element={<LazyRoute><RedemptionsPage /></LazyRoute>} />
            <Route path="kyc" element={<LazyRoute><KycPage /></LazyRoute>} />
          </Route>

          {/* M2 라이브 마켓 — 새 IA에서 내비 숨김, 직접 링크만 유지 (PRD Q3) */}
          <Route path="/marketplace">
            <Route index element={<LazyRoute><MarketplacePage /></LazyRoute>} />
            <Route path=":catalogId" element={<LazyRoute><MarketCardPage /></LazyRoute>} />
          </Route>
          <Route path="/agents" element={<LazyRoute><AgentsPage /></LazyRoute>} />
          {/* users.is_admin only — server-enforced via the Admin RPC tier */}
          <Route path="/admin" element={<LazyRoute><AdminPage /></LazyRoute>} />
          <Route path="/settings" element={<ComingSoonPage title="Settings" />} />

          {/* ── 레거시 딥링크 호환 — 구 플랫 경로를 새 계층으로 리다이렉트 ── */}
          <Route path="/draw/*" element={<Navigate to="/shop" replace />} />
          <Route path="/packs" element={<Navigate to="/profile/packs" replace />} />
          <Route path="/collection" element={<Navigate to="/profile/collection" replace />} />
          <Route path="/missions" element={<Navigate to="/profile/missions" replace />} />
          <Route path="/missions/links" element={<Navigate to="/profile/missions/links" replace />} />
          <Route path="/wallet" element={<Navigate to="/profile/wallet" replace />} />
          <Route path="/wallet/topup" element={<Navigate to="/profile/wallet/topup" replace />} />
          <Route path="/burn" element={<Navigate to="/profile/collection/burn" replace />} />
          <Route path="/redemptions" element={<Navigate to="/profile/redemptions" replace />} />
          <Route path="/kyc" element={<Navigate to="/profile/kyc" replace />} />
          <Route path="/redeem">
            <Route index element={<Navigate to="/profile/collection" replace />} />
            <Route path=":cardId" element={<RedeemLegacyRedirect />} />
          </Route>
          {/* 랜딩 푸터에서 링크 — 정식 법무 카피 확정 전까지 플레이스홀더 */}
          <Route path="/terms" element={<ComingSoonPage title="Terms of Service" />} />
          <Route path="/privacy" element={<ComingSoonPage title="Privacy Policy" />} />
          {/* Shell-internal 404 — a typo'd/stale in-app path (nested under
              AppShell) must 404 with chrome/balances/nav still visible, not
              silently drop a signed-in user out to the marketing landing page
              via the outer `*` below. */}
          <Route path="*" element={<ComingSoonPage title="Not found" />} />
        </Route>
        {/* Only reachable for paths outside the app shell entirely (should be
            effectively dead in practice — the nested `*` above wins first). */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
